import {
  PROTOCOL_VERSION,
  parseServerMessage,
  type ClientMessage,
  type ServerMessage,
  type WelcomeMessage,
} from './protocol';

export type MessageHandler = (message: ServerMessage) => void;
export type CloseHandler = (code: number, reason: string) => void;

/** Default gateway URL: same origin, `/ws` (Vite proxies it in dev, Caddy in production). */
export function defaultGatewayUrl(loc: Pick<Location, 'protocol' | 'host'> = location): string {
  return `${loc.protocol === 'https:' ? 'wss' : 'ws'}://${loc.host}/ws`;
}

/** Thin wrapper around the gateway WebSocket: handshake, typed send, typed receive. */
export class GatewayClient {
  private socket: WebSocket | null = null;
  private readonly messageHandlers = new Set<MessageHandler>();
  private readonly closeHandlers = new Set<CloseHandler>();

  constructor(
    private readonly url: string = defaultGatewayUrl(),
    private readonly clientName = `opensim-web-viewer/${__APP_VERSION__}`,
  ) {}

  /**
   * Opens the socket, says hello, resolves with the gateway's welcome. `event` is an event id
   * from the page's link (`eventFromSearch`); the welcome says whether the gateway knows it.
   */
  connect(event: string | null = null): Promise<WelcomeMessage> {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(this.url);
      this.socket = socket;
      let welcomed = false;

      socket.addEventListener('open', () => {
        this.send({
          type: 'hello',
          protocolVersion: PROTOCOL_VERSION,
          client: this.clientName,
          ...(event ? { event } : {}),
        });
      });

      socket.addEventListener('message', (event: MessageEvent<unknown>) => {
        if (typeof event.data !== 'string') return;
        const message = parseServerMessage(event.data);
        if (!message) return;
        if (!welcomed && message.type === 'welcome') {
          welcomed = true;
          resolve(message);
        }
        for (const handler of this.messageHandlers) handler(message);
      });

      socket.addEventListener('close', (event) => {
        if (!welcomed) reject(new Error(`Gateway closed before welcome (${event.code} ${event.reason})`));
        for (const handler of this.closeHandlers) handler(event.code, event.reason);
      });
    });
  }

  send(message: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  onClose(handler: CloseHandler): () => void {
    this.closeHandlers.add(handler);
    return () => this.closeHandlers.delete(handler);
  }

  close(): void {
    this.send({ type: 'logout' });
    this.socket?.close(1000, 'bye');
  }
}
